import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Eye, Pencil, Plus, Loader2 } from 'lucide-react';
import { schemaService } from '../../services';
import { FieldDefinitions, FieldDefinition, SectionConfig } from '../../types';
import SectionAccordion from '../../components/admin/SectionAccordion';
import SectionFormModal from '../../components/admin/SectionFormModal';
import FieldFormModal from '../../components/admin/FieldFormModal';

const SchemaEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [schema, setSchema] = useState<any>(null);
  const [fieldDefinitions, setFieldDefinitions] = useState<FieldDefinitions | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [globalActiveTab, setGlobalActiveTab] = useState<'general' | 'scoring' | 'llm' | null>(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState<{ key: string; config: SectionConfig } | null>(null);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [isFieldFormOpen, setIsFieldFormOpen] = useState(false);
  const [fieldFormDefaultSection, setFieldFormDefaultSection] = useState('');

  useEffect(() => {
    loadSchema();
  }, [id]);

  const loadSchema = async () => {
    if (!id) return;
    const data = await schemaService.get(id);
    setSchema(data);
    setFieldDefinitions(data.field_definitions || null);

    // Sélectionner la première section
    if (data.field_definitions?.sections) {
      const firstSection = Object.entries(data.field_definitions.sections)
        .sort((a, b) => a[1].order - b[1].order)[0];
      if (firstSection) setSelectedSection(firstSection[0]);
    }
  };

  const handleFieldChange = (fieldName: string, updatedField: FieldDefinition) => {
    if (!fieldDefinitions) return;

    setFieldDefinitions({
      ...fieldDefinitions,
      fields: {
        ...fieldDefinitions.fields,
        [fieldName]: updatedField
      }
    });
    setHasChanges(true);
  };

  const handleSectionSave = (key: string, config: SectionConfig) => {
    if (!fieldDefinitions) return;

    setFieldDefinitions({
      ...fieldDefinitions,
      sections: {
        ...fieldDefinitions.sections,
        [key]: config
      }
    });
    setHasChanges(true);
  };

  const handleSectionDelete = (key: string) => {
    if (!fieldDefinitions) return;

    // Check if any fields are using this section
    const fieldsInSection = Object.values(fieldDefinitions.fields).filter(f => f.ui.section === key);
    if (fieldsInSection.length > 0) {
      alert(`Impossible de supprimer: ${fieldsInSection.length} champ(s) utilisent cette section.`);
      return;
    }

    const { [key]: removed, ...remainingSections } = fieldDefinitions.sections;
    setFieldDefinitions({
      ...fieldDefinitions,
      sections: remainingSections
    });
    setHasChanges(true);

    // If deleted section was selected, select first available
    if (selectedSection === key) {
      const firstSection = Object.keys(remainingSections)[0];
      setSelectedSection(firstSection || null);
    }
  };

  const openSectionModal = (sectionKey?: string) => {
    if (sectionKey && fieldDefinitions?.sections[sectionKey]) {
      setEditingSection({ key: sectionKey, config: fieldDefinitions.sections[sectionKey] });
    } else {
      setEditingSection(null);
    }
    setShowSectionModal(true);
  };

  const handleSave = async () => {
    if (!id || !fieldDefinitions) return;
    setSaving(true);
    try {
      await schemaService.updateSchemaFieldDefinitions(id, fieldDefinitions);
      setHasChanges(false);
      // Recharger pour avoir le schema_json régénéré
      await loadSchema();
    } catch (error) {
      console.error('Error saving:', error);
    }
    setSaving(false);
  };

  const handleBootstrapFromGT = async () => {
    if (!id) return;
    setBootstrapping(true);
    try {
      await schemaService.bootstrapFromGT(id);
      await loadSchema();
    } catch (error) {
      console.error('Error bootstrapping:', error);
    }
    setBootstrapping(false);
  };

  const handleAddField = (fieldName: string, fieldDef: FieldDefinition) => {
    if (!fieldDefinitions) return;
    if (fieldName in fieldDefinitions.fields) return;
    setFieldDefinitions({
      ...fieldDefinitions,
      fields: { ...fieldDefinitions.fields, [fieldName]: fieldDef }
    });
    setHasChanges(true);
  };

  const handleDeleteField = (fieldName: string) => {
    if (!fieldDefinitions) return;
    const { [fieldName]: _, ...remaining } = fieldDefinitions.fields;
    setFieldDefinitions({ ...fieldDefinitions, fields: remaining });
    setHasChanges(true);
  };

  const openFieldForm = (sectionName: string) => {
    setFieldFormDefaultSection(sectionName);
    setIsFieldFormOpen(true);
  };

  const handlePreview = async () => {
    if (!id) return;
    const instructions = await schemaService.previewInstructions(id);
    setPreviewContent(instructions);
    setShowPreview(true);
  };

  // Grouper les champs par section
  const fieldsBySection = React.useMemo(() => {
    if (!fieldDefinitions?.fields) return {};

    const grouped: Record<string, Array<[string, FieldDefinition]>> = {};

    Object.entries(fieldDefinitions.fields).forEach(([name, field]) => {
      const section = field.ui.section;
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push([name, field]);
    });

    // Trier par display_order
    Object.values(grouped).forEach(fields => {
      fields.sort((a, b) => a[1].ui.display_order - b[1].ui.display_order);
    });

    return grouped;
  }, [fieldDefinitions]);

  const sortedSections = React.useMemo(() => {
    if (!fieldDefinitions?.sections) return [];
    return Object.entries(fieldDefinitions.sections)
      .sort((a, b) => a[1].order - b[1].order);
  }, [fieldDefinitions]);

  if (!schema) {
    return <div className="p-6">Chargement...</div>;
  }

  if (!fieldDefinitions || Object.keys(fieldDefinitions.fields || {}).length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-semibold">{schema.name}</h1>
            <p className="text-sm text-gray-500">{schema.domain} - v{schema.version}</p>
          </div>
        </div>
        <div className="p-6 flex justify-center">
          <div className="border rounded-lg p-8 bg-white text-center max-w-md">
            <p className="text-gray-600 mb-4">Aucun field_definitions configuré.</p>
            <button
              onClick={handleBootstrapFromGT}
              disabled={bootstrapping}
              className="px-4 py-2 bg-primary text-white rounded flex items-center gap-2 mx-auto disabled:opacity-50"
            >
              {bootstrapping ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Construction en cours...
                </>
              ) : (
                <>🔨 Construire depuis GT</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/parametres/schemas')}
            className="p-2 hover:bg-gray-100 rounded"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-semibold">{schema.name}</h1>
            <p className="text-sm text-gray-500">{schema.domain} - v{schema.version}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {hasChanges && (
            <span className="text-sm text-amber-600">Modifications non sauvegardées</span>
          )}
          <button
            onClick={handlePreview}
            className="px-4 py-2 border rounded flex items-center gap-2 hover:bg-gray-50"
          >
            <Eye size={16} />
            Preview Instructions
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="px-4 py-2 bg-primary text-white rounded flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={16} />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar - Sections */}
        <div className="w-64 bg-white border-r min-h-screen p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-gray-500">SECTIONS</h2>
            <button
              onClick={() => openSectionModal()}
              className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-blue-600"
              title="Ajouter une section"
            >
              <Plus size={16} />
            </button>
          </div>
          {sortedSections.map(([key, section]) => (
            <div
              key={key}
              className={`flex items-center justify-between px-3 py-2 rounded mb-1 group ${
                selectedSection === key ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
              }`}
            >
              <button
                onClick={() => setSelectedSection(key)}
                className="flex-1 text-left flex items-center justify-between"
              >
                <span>{section.label_fr}</span>
                <span className="text-xs text-gray-400">
                  {fieldsBySection[key]?.length || 0}
                </span>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); openSectionModal(key); }}
                className="ml-2 p-1 hover:bg-gray-200 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                title="Modifier la section"
              >
                <Pencil size={14} />
              </button>
            </div>
          ))}
        </div>

        {/* Main content - Fields */}
        <div className="flex-1 p-6">
          {/* Global Tab Switcher Buttons */}
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => setGlobalActiveTab('general')}
              className="px-3 py-1 text-sm bg-gray-100 border rounded hover:bg-gray-200"
            >
              📋 Tout → Général
            </button>
            <button
              onClick={() => setGlobalActiveTab('scoring')}
              className="px-3 py-1 text-sm bg-gray-100 border rounded hover:bg-gray-200"
            >
              ⚖️ Tout → Scoring
            </button>
            <button
              onClick={() => setGlobalActiveTab('llm')}
              className="px-3 py-1 text-sm bg-gray-100 border rounded hover:bg-gray-200"
            >
              🤖 Tout → Instructions LLM
            </button>
          </div>

          {selectedSection && fieldsBySection[selectedSection] && (
            <SectionAccordion
              sectionKey={selectedSection}
              section={fieldDefinitions.sections[selectedSection]}
              sections={fieldDefinitions.sections}
              fields={fieldsBySection[selectedSection]}
              onFieldChange={handleFieldChange}
              globalActiveTab={globalActiveTab}
              onTabChanged={() => setGlobalActiveTab(null)}
              onAddField={openFieldForm}
              onDeleteField={handleDeleteField}
            />
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-3/4 max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-semibold">Preview des Instructions LLM</h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-500">✕</button>
            </div>
            <pre className="p-4 overflow-auto max-h-[60vh] text-sm bg-gray-50">
              {previewContent}
            </pre>
          </div>
        </div>
      )}

      {/* Section Form Modal */}
      <SectionFormModal
        isOpen={showSectionModal}
        section={editingSection}
        existingKeys={Object.keys(fieldDefinitions.sections || {})}
        onClose={() => setShowSectionModal(false)}
        onSave={handleSectionSave}
        onDelete={handleSectionDelete}
      />

      {/* Field Form Modal */}
      <FieldFormModal
        isOpen={isFieldFormOpen}
        onClose={() => setIsFieldFormOpen(false)}
        onSubmit={handleAddField}
        existingFields={Object.keys(fieldDefinitions.fields || {})}
        defaultSection={fieldFormDefaultSection}
        sections={fieldDefinitions.sections || {}}
      />
    </div>
  );
};

export default SchemaEditorPage;
